import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate } from "../components/Navigate";
import { useIsAuthenticated, useMsal, useAccount } from "@azure/msal-react";
import { useHookstate } from "@hookstate/core";
import globalAuthStateStore from "../stores/globalAuthStateStore";
import { InteractionStatus, EventType } from "@azure/msal-browser";
import globalAppUserStore, {
  APIStatusType,
} from "../stores/globalAppUserStore";
import { useAppUserManager } from "../hooks/useManagers";
import { ProfileUpdateTypeEnum } from "../domains/profile/types/ProfileUpdateType.enum";
import config from "../config.json";

import _ from "lodash";
import PublicLayout from "../components/layout/PublicLayout";
import * as gaMgr from "../managers/GoogleAnalyticsManager";

// >>> GA4 ADD — import react-ga4 safely
import ReactGA from "react-ga4";

let _timeoutKey = -1;

export function useDefaultRoute(redirectUrl) {
  const navigate = useNavigate();
  const appUserMgr = useAppUserManager();
  useEffect(() => {
    if (appUserMgr?.AppUserState?.apiStatus == APIStatusType?.OnlineSecure) {
      if (redirectUrl) navigate(redirectUrl);
      else navigate(appUserMgr.defaultRoute());
    }
  }, [appUserMgr]);
}

export function useCheckAuth() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const globalAppUserState = useHookstate(globalAppUserStore);
  const appUser = globalAppUserState.get();
  const authErrorPath = "/AuthError";

  useEffect(() => {
    if (
      isAuthenticated &&
      appUser.initFailed &&
      location.pathname.toLowerCase() != authErrorPath.toLowerCase()
    ) {
      navigate(authErrorPath);
    }
  }, [appUser]);

  return isAuthenticated;
}

export function useAuthState() {
  const globalAuthState = useHookstate(globalAuthStateStore);
  return globalAuthState;
}

//Only call in app.js
export function useManageAuth() {
  const { instance, accounts, inProgress } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const globalAuthState = useHookstate(globalAuthStateStore);
  const authState = globalAuthState.get();

  // >>> GA4 ADD — initialize GA4 IMMEDIATELY with persisted user_id (fixes "(not set)")
  useEffect(() => {
    const persistedUid = localStorage.getItem("ga4_user_id");

    if (persistedUid) {
      ReactGA.initialize(gaMgr.TRACKING_ID, {
        gtagOptions: { user_id: persistedUid }
      });
    } else {
      ReactGA.initialize(gaMgr.TRACKING_ID);
    }
  }, []);
  // >>> END ADD

  if (
    !location.pathname.toLowerCase().startsWith("/logout") &&
    !location.pathname.toLowerCase().startsWith("/login") &&
    !location.pathname.toLowerCase().startsWith("/auth") &&
    location.pathname.toLowerCase() !== "/"
  ) {
    const fullUrl = location.pathname + location.search + location.hash;
    window.localStorage.setItem("LastUri", fullUrl);
  }

  const checkForToken = () => {
    const expireDateValue = authState?.idTokenClaims
      ? new Date(authState.idTokenClaims.exp * 1000)
      : null;

    const expiresIn =
      expireDateValue !== null
        ? expireDateValue?.getTime() - new Date().getTime()
        : 0;

    if (expiresIn !== null) {
      if (_timeoutKey > -1) clearTimeout(_timeoutKey);

      const refreshTokenTime = expiresIn - 300000; //5 minutes before expiry - increased from 15 seconds to 5 minutes to avoid edge cases
      _timeoutKey = setTimeout(() => {
        const newAuthState = {
          isAuthenticated,
          isAuthLoadComplete: inProgress === InteractionStatus.None,
        };
        if (newAuthState?.isAuthenticated && newAuthState?.isAuthLoadComplete) {
          const profileUpdateForceToken = sessionStorage.getItem(
            "profile-update-force-token"
          );
          if (profileUpdateForceToken) {
            sessionStorage.removeItem("profile-update-force-token");
          }

          instance
            .acquireTokenSilent({
              account: accounts[0],
              forceRefresh: profileUpdateForceToken !== null,
              scopes: [
                `https://${config.AUTH.ADB2C_TENANT_NAME}.onmicrosoft.com/core-api/core-api.access`,
              ],
            })
            .then(async (response) => {
              let accessToken = response.accessToken;
              let idToken = response.idToken;
              let idTokenClaims = response.idTokenClaims;
              const rawClaims = JSON.parse(JSON.stringify(idTokenClaims || authState?.idTokenClaims));
              const oid = rawClaims?.oid;
              //console.log("DEBUG OID:", oid, typeof oid);

              globalAuthState.merge({
                userId: oid,
                accessToken,
                isAuthLoadComplete: true,
                isAuthenticated: true,
                idToken,
                idTokenClaims,
                msalAccount: accounts[0],
              });

              // >>> GA4 ADD — update + persist user_id immediately
              if (oid) {
                localStorage.setItem("ga4_user_id", oid); // persist
                gaMgr.setGAUserId(oid);                   // live update
                try {
                  ReactGA.set({ user_id: oid });
                } catch (e) {
                  console.error("Failed to set GA user_id:", e);
                }
              }
              // >>> END ADD
            })
            .catch((error) => {
              console.log("Silent token acquisition failed, logging out.");
              localStorage.clear();
              sessionStorage.clear();
              gaMgr.resetGA4OnLogout();
              window.location.href = "/";
            });
        } else if (
          authState.isAuthenticated != newAuthState.isAuthenticated ||
          authState.isAuthLoadComplete != newAuthState.isAuthLoadComplete
        ) {
          globalAuthState.merge(newAuthState);
        }
      }, refreshTokenTime);
    }
  };
  
  useEffect(() => {
    if (accounts?.length) {
      globalAuthState.merge({
        msalAccount: accounts[0],
      });
    }
  }, [accounts]);

  useEffect(() => {
    checkForToken();
  }, [
    authState,
    globalAppUserStore,
    isAuthenticated,
    instance,
    accounts,
    inProgress,
    navigate,
  ]);

  useEffect(() => {
    const callbackId = instance.addEventCallback(async (message) => {
      if (message.eventType === EventType.LOGIN_FAILURE) {
        gaMgr.resetGA4OnLogout();
        navigate("/logout");
      } else if (message.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
        const profileUpdateType = sessionStorage.getItem("profile-update-type");

        const skipLogoutFromProfileScreen =
          profileUpdateType !== null &&
          profileUpdateType !== ProfileUpdateTypeEnum.PROFILE_CONFIGURE_MFA;

        if (profileUpdateType) {
          sessionStorage.removeItem("profile-update-type");
        }

        if (!skipLogoutFromProfileScreen) {
          gaMgr.resetGA4OnLogout();
          navigate("/logout");
        } else {
          sessionStorage.setItem("profile-update-force-token", true);
        }
      }
    });

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, []);

  return <PublicLayout></PublicLayout>;
}
